import { useCallback, useEffect, useState } from "react";
import api from "@/lib/api";
import { Button } from "@/components/ui/button";
import { RefreshCw } from "lucide-react";
import { DASH } from "@/constants/testIds";
import { toast } from "sonner";

const SECTIONS = [
  { key: "positive", title: "Positive Totality", accent: "#7B906F",
    description: "Constitutional strengths · resilient traits · favourable tendencies" },
  { key: "negative", title: "Negative Totality", accent: "#C15C3D",
    description: "Pathological tendencies · weak organs · vulnerabilities" },
  { key: "mixed", title: "Mixed Totality", accent: "#A8896C",
    description: "Dual-natured signatures · afflicted benefics · complex indicators" },
];

export default function HomeopathyPanel({ profileId }) {
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [refreshing, setRefreshing] = useState(false);

  const load = useCallback(async () => {
    setLoading(true);
    try { setData(await api.homeopathy(profileId)); }
    catch (e) {
      console.error("homeopathy failed:", e);
      toast.error(e?.response?.data?.detail || "Failed to load");
    }
    setLoading(false);
  }, [profileId]);
  const refresh = async () => {
    setRefreshing(true);
    try { setData(await api.refreshHomeo(profileId)); toast.success("Refreshed"); }
    catch (err) { console.error("refresh homeo failed:", err); toast.error("Refresh failed"); }
    setRefreshing(false);
  };
  useEffect(() => { load(); }, [load]);

  if (loading) return <div className="text-[#A19E95] font-mono text-sm">Distilling totalities…</div>;
  if (!data) return null;

  return (
    <div
      className="space-y-6 relative"
      style={{ backgroundImage: "url(https://images.pexels.com/photos/17323682/pexels-photo-17323682.png?auto=compress&cs=tinysrgb&dpr=2&h=650&w=940)",
               backgroundSize: "cover", backgroundBlendMode: "overlay", backgroundColor: "rgba(15,17,21,0.94)" }}
    >
      <div className="flex justify-end">
        <Button data-testid={DASH.refreshHomeo} onClick={refresh} disabled={refreshing}
                variant="ghost" className="text-[#A19E95] hover:text-[#D4AF37] hover:bg-transparent">
          <RefreshCw size={14} className={refreshing ? "animate-spin mr-2" : "mr-2"} /> Refresh
        </Button>
      </div>
      <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
        {SECTIONS.map(sec => {
          const blk = data[sec.key] || {};
          return (
            <div key={sec.key}
                 className="border-t-2 border border-[#2A2D35] bg-[#181A1F]/95 backdrop-blur-sm p-6"
                 style={{ borderTopColor: sec.accent }}
                 data-testid={`homeo-${sec.key}`}>
              <div className="text-xs uppercase tracking-widest font-mono" style={{ color: sec.accent }}>
                {sec.title}
              </div>
              <div className="text-[10px] text-[#6E6B64] mt-1">{sec.description}</div>
              <p className="mt-4 text-sm text-[#E6E1D6] font-light leading-relaxed">{blk.summary}</p>
              {(blk.traits || blk.pathologies || blk.dual_traits) && (
                <ul className="mt-3 space-y-1 text-xs text-[#A19E95] font-mono">
                  {(blk.traits || blk.pathologies || blk.dual_traits || []).map((t, i) => (
                    <li key={`${sec.key}-t-${i}-${String(t).slice(0,24)}`} className="pl-3 border-l-2 border-[#2A2D35]">{t}</li>
                  ))}
                </ul>
              )}
              {blk.organs_at_risk?.length > 0 && (
                <div className="mt-3 text-xs">
                  <div className="text-[10px] uppercase tracking-widest text-[#C15C3D]">Organs at Risk</div>
                  <div className="mt-1 text-[#E6E1D6]">{blk.organs_at_risk.join(" · ")}</div>
                </div>
              )}
              <div className="mt-5 pt-4 border-t border-[#2A2D35]">
                <div className="text-[10px] uppercase tracking-widest text-[#D4AF37] mb-2">
                  Kent-referenced Remedies
                </div>
                <div className="space-y-3">
                  {(blk.remedies || []).map((r, i) => (
                    <div key={`${sec.key}-r-${r.name || i}`} className="text-xs" data-testid={`remedy-${sec.key}-${i}`}>
                      <div className="font-heading text-sm text-[#E6E1D6]">{r.name}</div>
                      {r.rubric && <div className="italic text-[#A8896C] mt-0.5">{r.rubric}</div>}
                      <div className="text-[#A19E95] font-light mt-1">{r.why}</div>
                    </div>
                  ))}
                </div>
              </div>
            </div>
          );
        })}
      </div>
    </div>
  );
}
